import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { LanguageProvider } from "@/contexts/LanguageContext";

import Login from "@/pages/Login";
import Register from "@/pages/Register";
import RegisterCompany from "@/pages/RegisterCompany";
import Terms from "@/pages/Terms";
import Privacy from "@/pages/Privacy";
import Dashboard from "@/pages/Dashboard";
import LiveView from "@/pages/LiveView";
import Objects from "@/pages/Objects";
import Employees from "@/pages/Employees";
import Reports from "@/pages/Reports";
import Violations from "@/pages/Violations";
import Absences from "@/pages/Absences";
import MonthClosing from "@/pages/MonthClosing";
import AuditLog from "@/pages/AuditLog";
import Billing from "@/pages/Billing";
import Settings from "@/pages/Settings";
import Salaries from "@/pages/Salaries";
import SuperAdmin from "@/pages/SuperAdmin";
import Schedule from "@/pages/Schedule";
import MySchedule from "@/pages/MySchedule";
import MyHistory from "@/pages/MyHistory";
import MyNotes from "@/pages/MyNotes";
import MyObjects from "@/pages/MyObjects";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import AcceptInvite from "@/pages/AcceptInvite";
import Layout from "@/components/layout/Layout";

function RootRedirect() {
  const { user } = useAuth();
  if (user?.role === 'super_admin') return <Navigate to="/super-admin" replace />;
  return <Navigate to="/dashboard" replace />;
}

function RoleGuard({ roles, children }) {
  const { user } = useAuth();
  if (!user || !roles.includes(user.role)) return <Navigate to="/dashboard" replace />;
  return children;
}

function App() {
  return (
    <ThemeProvider>
      <LanguageProvider>
        <AuthProvider>
          <BrowserRouter>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/register-company" element={<RegisterCompany />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/accept-invite" element={<AcceptInvite />} />
              <Route element={<Layout />}>
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/live" element={<RoleGuard roles={['admin','hr','controller']}><LiveView /></RoleGuard>} />
                <Route path="/live/:objectId" element={<RoleGuard roles={['admin','hr','controller']}><LiveView /></RoleGuard>} />
                <Route path="/objects" element={<RoleGuard roles={['admin']}><Objects /></RoleGuard>} />
                <Route path="/employees" element={<RoleGuard roles={['admin','hr']}><Employees /></RoleGuard>} />
                <Route path="/reports" element={<RoleGuard roles={['admin','hr','controller']}><Reports /></RoleGuard>} />
                <Route path="/violations" element={<RoleGuard roles={['admin','hr','controller','employee']}><Violations /></RoleGuard>} />
                <Route path="/absences" element={<RoleGuard roles={['admin','hr','controller']}><Absences /></RoleGuard>} />
                <Route path="/months" element={<RoleGuard roles={['admin','hr']}><MonthClosing /></RoleGuard>} />
                <Route path="/audit" element={<RoleGuard roles={['admin']}><AuditLog /></RoleGuard>} />
                <Route path="/billing" element={<RoleGuard roles={['admin','super_admin']}><Billing /></RoleGuard>} />
                <Route path="/salaries" element={<RoleGuard roles={['admin']}><Salaries /></RoleGuard>} />
                <Route path="/super-admin" element={<RoleGuard roles={['super_admin']}><SuperAdmin /></RoleGuard>} />
                <Route path="/schedule" element={<RoleGuard roles={['admin','hr']}><Schedule /></RoleGuard>} />
                <Route path="/my-schedule" element={<RoleGuard roles={['employee']}><MySchedule /></RoleGuard>} />
                <Route path="/my-objects" element={<RoleGuard roles={['employee']}><MyObjects /></RoleGuard>} />
                <Route path="/my-history" element={<RoleGuard roles={['employee']}><MyHistory /></RoleGuard>} />
                <Route path="/my-notes" element={<RoleGuard roles={['employee']}><MyNotes /></RoleGuard>} />
                <Route path="/settings" element={<RoleGuard roles={['admin','super_admin']}><Settings /></RoleGuard>} />
              </Route>
              <Route path="/" element={<RootRedirect />} />
              <Route path="*" element={<RootRedirect />} />
            </Routes>
          </BrowserRouter>
          <Toaster position="top-right" richColors closeButton toastOptions={{ className: 'font-sans' }} />
        </AuthProvider>
      </LanguageProvider>
    </ThemeProvider>
  );
}

export default App;
